import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { env, REACT_APP_SERVER_API } from 'src/config';
import { getHeaders } from 'src/common';
import './payment-bell.css';

const labels = {
  az: {
    notifications: 'Bildirişlər', allRead: 'Hamısını oxunmuş kimi işarələ',
    paymentTitle: 'Ödəniş təsdiqləndi',
    paymentBody: 'Ödənişiniz təsdiqləndi. Üzvlük paketiniz hesabınıza əlavə edildi.',
    empty: 'Hələ bildiriş yoxdur.', loadError: 'Bildirişləri yükləmək mümkün olmadı.',
    retry: 'Yenidən yoxla', read: 'Oxunmuş kimi işarələ', close: 'Bağla',
  },
  ru: {
    notifications: 'Уведомления', allRead: 'Отметить все как прочитанные',
    paymentTitle: 'Платёж подтверждён',
    paymentBody: 'Ваш платёж подтверждён. Пакет подписки добавлен в ваш аккаунт.',
    empty: 'Уведомлений пока нет.', loadError: 'Не удалось загрузить уведомления.',
    retry: 'Повторить', read: 'Отметить как прочитанное', close: 'Закрыть',
  },
  en: {
    notifications: 'Notifications', allRead: 'Mark all as read',
    paymentTitle: 'Payment confirmed',
    paymentBody: 'Your payment was accepted. Your membership package has been added to your account.',
    empty: 'No notifications yet.', loadError: 'Could not load notifications.',
    retry: 'Try again', read: 'Mark as read', close: 'Close',
  },
};

function formatDate(value, language) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try { return new Intl.DateTimeFormat(language === 'az' ? 'az-AZ' : language === 'ru' ? 'ru-RU' : 'en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  }).format(date); } catch (_) { return ''; }
}

export default function PaymentBell({ userId, theme, buttonClassName = '' }) {
  const { i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0].toLowerCase();
  const lang = labels[language] ? language : 'en';
  const copy = labels[lang];
  // The JWT can identify a signed-in user even when the UI user object has no `id`.
  // Inbox endpoints authorize by the JWT, never by this client-side prop.
  const signedIn = Boolean(userId || (typeof window !== 'undefined' && window.localStorage.getItem('access_token')));
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [toast, setToast] = useState(false);
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);
  const refreshRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!signedIn) {
      setAlerts([]);
      setUnread(0);
      setOpen(false);
      setToast(false);
      refreshRef.current = null;
      return undefined;
    }
    let alive = true;
    let initialized = false;
    let highestId = 0;
    let sequence = 0;
    const refresh = async () => {
      if (document.visibilityState === 'hidden') return;
      const current = ++sequence;
      try {
        const response = await axios.get(env(REACT_APP_SERVER_API) + 'notification/user_inbox', { headers: getHeaders() });
        if (!alive || current !== sequence) return;
        const result = response?.data?.result || {};
        const incoming = Array.isArray(result.alerts)
          ? result.alerts.filter(item => Number.isSafeInteger(Number(item.id)))
          : [];
        const unreadAlerts = incoming.filter(item => item.read_at == null);
        const nextHighest = incoming.reduce((max, item) => Math.max(max, Number(item.id)), 0);
        if (initialized && unreadAlerts.some(item => Number(item.id) > highestId)) {
          setToast(true);
          if (timerRef.current) window.clearTimeout(timerRef.current);
          timerRef.current = window.setTimeout(() => setToast(false), 7500);
        }
        highestId = Math.max(highestId, nextHighest);
        initialized = true;
        setAlerts(unreadAlerts);
        setUnread(Math.max(0, Number(result.unread_count) || 0));
        setLoadError(false);
      } catch (_) {
        if (alive && current === sequence) setLoadError(true);
      }
    };
    refreshRef.current = refresh;
    refresh();
    const poll = window.setInterval(refresh, 20000);
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      sequence++;
      refreshRef.current = null;
      window.clearInterval(poll);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [signedIn, userId]);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    const escape = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);

  async function markRead(id) {
    if (saving) return;

    // Optimistic UI: once the user clicks the notification it disappears immediately.
    setAlerts(current => current.filter(item => Number(item.id) !== Number(id)));
    setUnread(current => Math.max(0, current - 1));
    setToast(false);
    setSaving(true);

    try {
      await axios.post(
        env(REACT_APP_SERVER_API) + 'notification/user_read',
        { id },
        { headers: getHeaders() },
      );
      setLoadError(false);
      if (refreshRef.current) await refreshRef.current();
    } catch (_) {
      setLoadError(true);
      // If the server rejected the change, reload the real inbox state.
      if (refreshRef.current) await refreshRef.current();
    } finally {
      setSaving(false);
    }
  }

  async function markAllRead() {
    if (saving) return;

    setAlerts([]);
    setUnread(0);
    setToast(false);
    setSaving(true);

    try {
      await axios.post(
        env(REACT_APP_SERVER_API) + 'notification/user_read_all',
        {},
        { headers: getHeaders() },
      );
      setLoadError(false);
      if (refreshRef.current) await refreshRef.current();
    } catch (_) {
      setLoadError(true);
      if (refreshRef.current) await refreshRef.current();
    } finally {
      setSaving(false);
    }
  }

  if (!signedIn) return null;
  return (
    <div className={'oc-payment-bell-wrap' + (theme === 'theme-dark' ? ' oc-payment-bell-dark' : '')} ref={wrapRef}>
      <button type='button' className={buttonClassName + ' oc-payment-bell-trigger'}
        aria-label={copy.notifications + (unread ? ' (' + unread + ')' : '')}
        title={copy.notifications} aria-expanded={open} aria-haspopup='dialog'
        onClick={() => { setOpen(value => !value); if (refreshRef.current) refreshRef.current(); }}>
        <svg width='25' height='25' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8'
          strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
          <path d='M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9' />
          <path d='M10 21h4' />
        </svg>
        {unread > 0 && <span className='oc-payment-bell-badge' aria-hidden='true'>{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && <div className='oc-payment-bell-panel' role='dialog' aria-label={copy.notifications}>
        <div className='oc-payment-bell-head'>
          <strong>{copy.notifications}</strong>
          <div className='oc-payment-bell-head-actions'>
            {unread > 0 && <button type='button' disabled={saving} onClick={markAllRead}>{copy.allRead}</button>}
            <button type='button' className='oc-payment-bell-dismiss' aria-label={copy.close} onClick={() => setOpen(false)}>×</button>
          </div>
        </div>
        {loadError && <div className='oc-payment-bell-error' role='status'>
          {copy.loadError} <button type='button' onClick={() => refreshRef.current && refreshRef.current()}>{copy.retry}</button>
        </div>}
        {alerts.length === 0 && !loadError && <p className='oc-payment-bell-empty'>{copy.empty}</p>}
        <div className='oc-payment-bell-items'>
          {alerts.map(alert => (
            <div
              className='oc-payment-bell-item is-unread'
              key={alert.id}
              role='button'
              tabIndex={0}
              aria-label={copy.read}
              title={copy.read}
              onClick={() => markRead(alert.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  markRead(alert.id);
                }
              }}
            >
              <div className='oc-payment-bell-item-icon' aria-hidden='true'>✓</div>
              <div className='oc-payment-bell-item-text'>
                <div className='oc-payment-bell-item-title'>{copy.paymentTitle}</div>
                <p>{copy.paymentBody}</p>
                <time dateTime={alert.created_at}>{formatDate(alert.created_at, lang)}</time>
              </div>
            </div>
          ))}
        </div>
      </div>}
      {toast && <div className='oc-payment-bell-toast' role='status'>
        <span aria-hidden='true'>✓</span><div><strong>{copy.paymentTitle}</strong><p>{copy.paymentBody}</p></div>
        <button type='button' aria-label={copy.close} onClick={() => setToast(false)}>×</button>
      </div>}
    </div>
  );
}
